import { useMutation, useQuery } from "@apollo/client";
import { PencilIcon } from "@heroicons/react/outline";
import { captureException, withErrorBoundary } from "@sentry/react";
import { compact, omit, reverse, sortBy, take } from "lodash";
import { useCallback, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import { RxReset } from "react-icons/rx";
import ReactMarkdown from "react-markdown";
import {
  GetMyWorkHistoryQuery,
  GetMyWorkHistoryQueryVariables,
  MyWorkHistoryItemFragmentFragment,
  UpdateMyWorkHistoryMutation,
  UpdateMyWorkHistoryMutationVariables,
  WorkHistorySummaryInput,
  WorkHistorySummarySectionNode,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import ProfileEmptyState from "@apps/profile/profile-empty-state";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Avatars from "@components/avatar/avatars";
import Button from "@components/button/button";
import CollapsibleContainer from "@components/collapsible-container/collapsible-container";
import ErrorFallback from "@components/error-fallback/error-fallback";
import Input from "@components/input/input";
import Loading from "@components/loading/loading";
import TextareaAutosize from "@components/textarea-autosize/textarea-autosize";
import Tooltip from "@components/tooltip/tooltip";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import getMyWorkHistoryQuery from "../graphql/get-my-work-history-query";
import updateWorkHistoryMutation from "../graphql/update-work-history-mutation";
import {
  WorkHistoryExternalEventObject,
  WorkHistoryExternalObjectTitles,
} from "./helpers";
import ExternalEventObject from "./work-history-external-object";
import WorkHistorySummaryHeader from "./work-history-summary-header";

type MyWorkHistorySection = Pick<
  WorkHistorySummarySectionNode,
  "id" | "title" | "content" | "hidden"
> & { resetAvailable: boolean };

const WorkHistorySidebar = ({
  userId,
  editable,
  startDate,
  endDate,
  forceDateFilter,
  hideSummaryHeader = false,
  sectionClassName = "",
  topCollaboratorAvatarSize = 8,
  onCompletedFetchingHistory,
  hasViewingPermission = true,
}: {
  userId?: number;
  editable?: boolean;
  startDate: string;
  endDate: string;
  forceDateFilter?: boolean;
  hideSummaryHeader?: boolean;
  sectionClassName?: string;
  topCollaboratorAvatarSize?: number;
  onCompletedFetchingHistory?: () => void;
  hasViewingPermission?: boolean;
}) => {
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();

  const [summary, setSummary] = useState<string>("");
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [isEditingSectionIndex, setIsEditingSectionIndex] = useState<
    number | null
  >(null);
  const [summaryResetAvailable, setSummaryResetAvailable] = useState(false);
  const [workHistoryItem, setWorkHistoryItem] =
    useState<MyWorkHistoryItemFragmentFragment | null>(null);
  const [workHistorySections, setWorkHistorySections] = useState<
    MyWorkHistorySection[]
  >([]);
  const [externalEventData, setExternalEventData] = useState<
    WorkHistoryExternalEventObject[]
  >([]);
  const [externalEventDataSummaries, setExternalEventDataSummaries] = useState<{
    [key: string]: string;
  }>({});
  const [topCollaborators, setTopCollaborators] = useState<BasicUser[]>([]);

  const updateWorkHistoryLocalData = useCallback(
    (newWorkHistoryItem?: MyWorkHistoryItemFragmentFragment | null) => {
      if (newWorkHistoryItem) {
        const sections = assertEdgesNonNull(newWorkHistoryItem.sections).map(
          (section) => ({
            id: section.id,
            title: section.modifiedTitle || section.title,
            content: section.modifiedContent || section.content,
            hidden: section.hidden,
            resetAvailable:
              !!section.modifiedTitle || !!section.modifiedContent,
          }),
        );

        setWorkHistoryItem(newWorkHistoryItem);
        setSummary(
          newWorkHistoryItem.modifiedSummary || newWorkHistoryItem.summary,
        );
        setSummaryResetAvailable(!!newWorkHistoryItem.modifiedSummary);
        setWorkHistorySections(sections);

        const collaboratorData = newWorkHistoryItem.collaborators.edges
          .map((edge) => assertNonNull(edge?.node))
          .filter((collaborator) => collaborator.user);
        const sortedCollaborators = compact(
          reverse(sortBy(collaboratorData, "collaborationCount")).map(
            (collaborator) => collaborator,
          ),
        );

        // Get the third most frequent collaborator (or second or first if there are less than three items). Then
        // set a lower bound for collaboration frequency as 90% of our lower bound, and take all of the collaborators
        // who meet that threshold. The intention of this is to get somewhere between 3-10 collaborators,
        // but not too just blindly take the first 10 if there is a long tail of people who the user only had a couple
        // of meetings with and only a small number that they had frequent meetings with
        const thresholdCount =
          sortedCollaborators[Math.min(2, sortedCollaborators.length - 1)]
            .collaborationCount;
        const tolerance = 0.1;
        const collaborationCountLowerBound = thresholdCount * (1 - tolerance);
        const collaboratorsAboveLowerBound: BasicUser[] = sortedCollaborators
          .filter(
            (collaborator) =>
              collaborator.collaborationCount >= collaborationCountLowerBound,
          )
          .map((collaborator) => assertNonNull(collaborator.user));
        setTopCollaborators(take(collaboratorsAboveLowerBound, 10));

        try {
          const externalEventObjects = JSON.parse(
            newWorkHistoryItem.externalEventData,
          ) as WorkHistoryExternalEventObject[];
          setExternalEventData(externalEventObjects);
        } catch (e) {
          captureException(e);
        }
        try {
          const externalEventSummaryData = JSON.parse(
            newWorkHistoryItem.externalEventDataSummaries,
          );
          setExternalEventDataSummaries(externalEventSummaryData);
        } catch (e) {
          captureException(e);
        }
      }
    },
    [],
  );

  const { loading } = useQuery<
    GetMyWorkHistoryQuery,
    GetMyWorkHistoryQueryVariables
  >(getMyWorkHistoryQuery, {
    variables: {
      organizationId: organization.id,
      userId: userId ?? currentUser.id,
      start: startDate,
      end: endDate,
      forceDateFilter: forceDateFilter,
    },
    skip: !hasViewingPermission || !organization.featureFlags.workHistory,
    context: {
      errorMatches: [
        {
          match: "You do not have permission to access this user",
          title:
            "You do not have permission to view the work history of this employee.",
        },
        {
          match: "User is not a member of the organization",
          title:
            "This user has not joined TopicFlow yet or does not belong to your organization.",
        },
      ],
    },
    onError: () => {
      onCompletedFetchingHistory?.();
    },
    onCompleted: (data) => {
      const workHistoryItem = data?.workHistory;
      updateWorkHistoryLocalData(workHistoryItem);
      onCompletedFetchingHistory?.();
    },
  });

  const [saveWorkHistory, { loading: isSavingWorkHistory }] = useMutation<
    UpdateMyWorkHistoryMutation,
    UpdateMyWorkHistoryMutationVariables
  >(updateWorkHistoryMutation);

  const handleUpdateWorkHistory = useCallback(
    (workHistoryData: WorkHistorySummaryInput) => {
      if (!workHistoryItem) {
        return;
      }

      saveWorkHistory({
        variables: {
          id: workHistoryItem.id,
          data: workHistoryData,
        },
        onCompleted: (data) => {
          const workHistoryItem = data?.updateWorkHistory?.workHistorySummary;
          updateWorkHistoryLocalData(workHistoryItem);
          setIsEditingSummary(false);
          setIsEditingSectionIndex(null);
        },
      });
    },
    [saveWorkHistory, updateWorkHistoryLocalData, workHistoryItem],
  );

  const handleResetSummaryToOriginal = useCallback(() => {
    handleUpdateWorkHistory({
      // use blank string to reset to original
      summary: "",
    });
  }, [handleUpdateWorkHistory]);

  const handleCancelSummaryEdit = useCallback(() => {
    if (!workHistoryItem) {
      return;
    }

    setSummary(workHistoryItem.modifiedSummary || workHistoryItem.summary);
    setIsEditingSummary(false);
  }, [workHistoryItem]);

  const handleSaveSummaryEdit = useCallback(() => {
    handleUpdateWorkHistory({
      summary: summary,
    });
  }, [handleUpdateWorkHistory, summary]);

  const handleChangeSectionVisibility = useCallback(
    (sectionId: number, hidden: boolean) => {
      const existingSection = workHistorySections.find(
        (section) => section.id === sectionId,
      );
      if (!existingSection) {
        return;
      }

      handleUpdateWorkHistory({
        sections: [{ ...omit(existingSection, "resetAvailable"), hidden }],
      });
    },
    [handleUpdateWorkHistory, workHistorySections],
  );

  const handleResetSectionToOriginal = useCallback(
    (sectionId: number) => {
      const existingSection = workHistorySections.find(
        (section) => section.id === sectionId,
      );
      if (!existingSection) {
        return;
      }

      handleUpdateWorkHistory({
        sections: [
          {
            ...omit(existingSection, "resetAvailable"),
            title: "",
            content: "",
          },
        ],
      });
    },
    [handleUpdateWorkHistory, workHistorySections],
  );

  const handleUpdateSection = useCallback(
    (sectionIndex: number, data: { title?: string; content?: string }) => {
      const existingSection = workHistorySections[sectionIndex];
      if (!existingSection) {
        return;
      }

      setWorkHistorySections(
        workHistorySections.map((section, index) => {
          if (index === sectionIndex) {
            return {
              ...section,
              ...data,
            };
          }
          return section;
        }),
      );
    },
    [workHistorySections],
  );

  const handleSaveSection = useCallback(
    (sectionIndex: number) => {
      const existingSection = workHistorySections[sectionIndex];
      if (!existingSection) {
        return;
      }

      handleUpdateWorkHistory({
        sections: [{ ...omit(existingSection, "resetAvailable") }],
      });
    },
    [handleUpdateWorkHistory, workHistorySections],
  );

  const handleCancelSectionEdit = useCallback(() => {
    if (!workHistoryItem) {
      return;
    }
    setIsEditingSectionIndex(null);
    setWorkHistorySections(
      assertEdgesNonNull(workHistoryItem.sections).map((section) => ({
        id: section.id,
        title: section.modifiedTitle || section.title,
        content: section.modifiedContent || section.content,
        hidden: section.hidden,
        resetAvailable: !!section.modifiedTitle || !!section.modifiedContent,
      })),
    );
  }, [workHistoryItem]);

  if (!organization.featureFlags.workHistory) {
    return null;
  }

  if (loading) {
    return (
      <div className="px-8 py-4 w-full mx-auto">
        <Loading />
      </div>
    );
  }

  if (!hasViewingPermission) {
    return (
      <ProfileEmptyState>
        You do not have permission to view the work history of this employee.
      </ProfileEmptyState>
    );
  }

  if (!workHistoryItem) {
    return (
      <ProfileEmptyState>
        No work history summary available. Please check back later.
      </ProfileEmptyState>
    );
  }

  const externalEventDataSummariesKeys = Object.keys(
    externalEventDataSummaries,
  );

  return (
    <div className="w-full">
      {!hideSummaryHeader && (
        <WorkHistorySummaryHeader workHistoryItem={workHistoryItem} />
      )}

      <div
        className={classNames(
          "px-8 py-4 text-sm border-b flex justify-between",
          sectionClassName,
        )}
      >
        <div className="flex-1">
          <div className="font-medium mb-2">Time spent in meetings</div>
          <div className="text-gray-500">
            {`${workHistoryItem.meetingTimeHours} hours (${workHistoryItem.meetingCount} meetings)`}
          </div>
        </div>
        {topCollaborators.length > 0 && (
          <div>
            <div className="font-medium mb-2">Top collaborators</div>
            <div className="flex gap-1 justify-end items-center">
              <Avatars
                users={topCollaborators}
                size={topCollaboratorAvatarSize}
                max={topCollaborators.length + 1}
              />
            </div>
          </div>
        )}
      </div>

      <div
        className={classNames(
          "px-8 py-4 text-sm border-b last:border-b-0 relative",
          sectionClassName,
        )}
      >
        {!isEditingSummary && isEditingSectionIndex === null && editable && (
          <div className="top-2 right-2 absolute flex items-center gap-2">
            {summaryResetAvailable && (
              <Tooltip text="Reset to original">
                <button
                  className="text-gray-500 p-2 rounded hover:bg-gray-100"
                  onClick={handleResetSummaryToOriginal}
                >
                  <RxReset className="w-4 h-4" />
                </button>
              </Tooltip>
            )}
            <Tooltip text="Edit">
              <button
                className="text-gray-500 p-2 rounded hover:bg-gray-100"
                onClick={() => setIsEditingSummary(true)}
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            </Tooltip>
          </div>
        )}
        <div className="font-medium mb-2">Summary</div>
        {isEditingSummary ? (
          <>
            <TextareaAutosize
              className="w-full border rounded-md p-2 resize-none"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                text="Cancel"
                onClick={handleCancelSummaryEdit}
              />
              <Button
                type="button"
                text="Save"
                theme="primary"
                disabled={isSavingWorkHistory}
                onClick={handleSaveSummaryEdit}
              />
            </div>
          </>
        ) : (
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown>{summary}</ReactMarkdown>
          </div>
        )}
      </div>

      {workHistorySections.map((section, index) => (
        <div
          key={section.id}
          className={classNames(
            "px-8 py-4 text-sm border-b last:border-b-0 relative",
            section.hidden && "bg-gray-200",
            sectionClassName,
          )}
        >
          {!isEditingSummary && isEditingSectionIndex === null && editable && (
            <div className="top-2 right-2 absolute flex items-center gap-2">
              {section.resetAvailable && !section.hidden && (
                <Tooltip text="Reset to original">
                  <button
                    className="text-gray-500 p-2 rounded hover:bg-gray-100"
                    onClick={() => handleResetSectionToOriginal(section.id)}
                  >
                    <RxReset className="w-4 h-4" />
                  </button>
                </Tooltip>
              )}
              <Tooltip text={section.hidden ? "Show" : "Hide"}>
                <button
                  className="text-gray-500 p-2 rounded hover:bg-gray-100"
                  onClick={() =>
                    handleChangeSectionVisibility(section.id, !section.hidden)
                  }
                >
                  {section.hidden ? (
                    <FaEye className="w-4 h-4" />
                  ) : (
                    <FaEyeSlash className="w-4 h-4" />
                  )}
                </button>
              </Tooltip>
              {!section.hidden && (
                <Tooltip text="Edit">
                  <button
                    className="text-gray-500 p-2 rounded hover:bg-gray-100"
                    onClick={() => setIsEditingSectionIndex(index)}
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                </Tooltip>
              )}
            </div>
          )}
          {isEditingSectionIndex === index ? (
            <>
              <Input
                value={section.title}
                onChange={(e) => {
                  const newTitle = e.target.value;
                  handleUpdateSection(index, { title: newTitle });
                }}
              />
              <TextareaAutosize
                className="mt-1 w-full border rounded-md p-2 resize-none"
                value={section.content}
                onChange={(e) => {
                  const newContent = e.target.value;
                  handleUpdateSection(index, { content: newContent });
                }}
              />
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  text="Cancel"
                  onClick={handleCancelSectionEdit}
                />
                <Button
                  type="button"
                  text="Save"
                  theme="primary"
                  disabled={isSavingWorkHistory}
                  onClick={() => handleSaveSection(index)}
                />
              </div>
            </>
          ) : (
            <>
              <div className="font-medium mb-2">{section.title}</div>
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown>{section.content}</ReactMarkdown>
              </div>
            </>
          )}
        </div>
      ))}

      {(externalEventData.length > 0 ||
        externalEventDataSummariesKeys.length > 0) && (
        <div
          className={classNames("px-8 py-4 text-sm border-b", sectionClassName)}
        >
          <div className="font-medium mb-4">From integrations:</div>

          <div className="flex flex-col gap-2">
            {externalEventData.map(
              (eventObject: WorkHistoryExternalEventObject) => {
                if (eventObject.type in externalEventDataSummaries) {
                  return null;
                }
                return (
                  <ExternalEventObject
                    key={eventObject.id}
                    eventObject={eventObject}
                  />
                );
              },
            )}
          </div>

          {Object.keys(externalEventDataSummaries).map((key) => {
            // Perform some operation on each key
            return (
              <div className="flex flex-col gap-2 py-4" key={key}>
                <div className="font-bold">
                  {WorkHistoryExternalObjectTitles[key]}
                </div>
                <div className="whitespace-pre-line">
                  {externalEventDataSummaries[key]}
                </div>
                <CollapsibleContainer title="Details" startOpen={false}>
                  <div className="flex flex-col gap-2 p-2">
                    {externalEventData.map(
                      (eventObject: WorkHistoryExternalEventObject) => {
                        if (eventObject.type === key) {
                          return (
                            <ExternalEventObject
                              key={eventObject.id}
                              eventObject={eventObject}
                            />
                          );
                        }
                      },
                    )}
                  </div>
                </CollapsibleContainer>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const WorkHistorySidebarWithSentry = withErrorBoundary(WorkHistorySidebar, {
  fallback: ErrorFallback,
});

export default WorkHistorySidebarWithSentry;
